import { computePosition } from '@floating-ui/dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FloatingMenu, FloatingMenuCoords } from '../components/FloatingMenu';
import { usePointerInteractions } from '../hooks/usePointerInteractions';

const DOM_ELEMENT = document.body;

interface FloatingMenuPluginProps {
  includedControls?: string[];
}

/**
 * A plugin that renders a floating menu for text selection in a Lexical editor
 * The menu appears when text is selected and provides formatting options
 *
 * @param {Object} props - The component props
 * @param {string[]} props.includedControls - Optional array of control names to include in the floating menu
 * @returns {ReactPortal} A portal containing the floating menu component
 */
export function FloatingMenuPlugin({ includedControls }: FloatingMenuPluginProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<FloatingMenuCoords>(undefined);
  const [editor] = useLexicalComposerContext();

  const { isPointerDown, isPointerReleased } = usePointerInteractions();

  /**
   * Effect hook that handles clicks outside the floating menu
   * Adds a mousedown event listener to hide the menu when clicking outside
   * Cleans up the event listener on unmount
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setCoords(undefined);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculatePosition = useCallback(() => {
    const domSelection = getSelection();
    const domRange = domSelection?.rangeCount !== 0 && domSelection?.getRangeAt(0);

    if (!domRange || !ref.current || isPointerDown) return setCoords(undefined);

    computePosition(domRange, ref.current, { placement: 'top' })
      .then(pos => {
        const toolbarWidth = ref.current?.offsetWidth || 0;
        const viewportWidth = window.innerWidth;
        const rightMargin = 10;

        let adjustedX = pos.x;

        // Check if the toolbar overflows the right edge of the viewport
        if (adjustedX + toolbarWidth > viewportWidth) {
          adjustedX = viewportWidth - toolbarWidth - rightMargin;
        }

        // Check if the toolbar overflows the left edge of the viewport
        if (adjustedX < 0) {
          adjustedX = 0;
        }

        setCoords({ x: adjustedX, y: pos.y - 10 });
      })
      .catch(() => {
        setCoords(undefined);
      });
  }, [isPointerDown]);

  const $handleSelectionChange = useCallback(() => {
    if (editor.isComposing() || editor.getRootElement() !== document.activeElement) {
      setCoords(undefined);
      return;
    }

    const selection = $getSelection();

    if ($isRangeSelection(selection) && !selection.anchor.is(selection.focus)) {
      calculatePosition();
    } else {
      setCoords(undefined);
    }
  }, [editor, calculatePosition]);

  useEffect(() => {
    const unregisterListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => $handleSelectionChange());
    });
    return unregisterListener;
  }, [editor, $handleSelectionChange]);

  const show = coords !== undefined;

  useEffect(() => {
    if (!show && isPointerReleased) {
      editor.getEditorState().read(() => $handleSelectionChange());
    }
    // Adding show to the dependency array causes an issue if
    // a range selection is dismissed by navigating via arrow keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPointerReleased, $handleSelectionChange, editor]);

  return createPortal(
    <FloatingMenu
      includedControls={includedControls}
      calculatePosition={calculatePosition}
      ref={ref}
      editor={editor}
      coords={coords}
    />,
    DOM_ELEMENT,
  );
}
